import { getToken, useAuthContext } from 'contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';
import { UserUrls } from 'services/api/urls';
import { AccessType } from 'types/User';

/**
 * Hook pour gérer l'affichage et le chargement du logo du Account de l'utilisateur.
 * @returns { show: boolean, imageUrl: string | null, handleError: () => void }
 * - show: indique si le logo doit être affiché
 * - imageUrl: URL blob de l'image chargée avec authentification
 * - handleError: fonction à appeler en cas d'erreur de chargement de l'image
 */
export const useAccountLogo = () => {
  const { auth } = useAuthContext();
  const [show, setShow] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const isConcessionnaire =
    auth?.user?.accessType === AccessType.CONCESSIONNAIRE;

  const handleError = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    // Only load logo for concessionnaire users
    if (!isConcessionnaire) {
      return;
    }

    let isMounted = true;

    const loadImage = async () => {
      try {
        const token = getToken();
        const response = await fetch(UserUrls.GET_ACCOUNT_LOGO, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (isMounted) {
            setShow(false);
          }
          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (isMounted) {
          setImageUrl(url);
          setShow(true);
        } else {
          // Clean up if component unmounted during fetch
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error loading account logo:', error);
        if (isMounted) {
          setShow(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [isConcessionnaire]);

  return { show, imageUrl, handleError } as const;
};
